import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import axios from "axios";
import { Upload, Ruler, ArrowUp, ArrowDown, MapPin, ArrowLeft, AlertTriangle } from "lucide-react";
import RegionMap from "@/components/RegionMap";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function readUser() {
  try { return JSON.parse(sessionStorage.getItem("mod_user") || "null"); } catch { return null; }
}
function isMaster(u) {
  return !!u && (u.role === "master" || u.role === "admin" || u.is_admin === true);
}

export default function GpxVerktyg() {
  const [token] = useState(sessionStorage.getItem("mod_token") || "");
  const [user] = useState(readUser());
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const [tileLayer, setTileLayer] = useState("standard");
  const [result, setResult] = useState(null); // { url, geojson, stats }
  // Build 94c.5 — Sicherheitsbestätigung
  const [confirmed, setConfirmed] = useState(false);
  const [note, setNote] = useState("");

  if (!token) return <Navigate to="/mod" replace />;
  if (!isMaster(user)) return <Navigate to="/mod" replace />;

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!confirmed) {
      setError("Bekräfta först att källa, område och rättigheter är kontrollerade.");
      e.target.value = "";
      return;
    }
    setError(null);
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      if (note.trim()) fd.append("note", note.trim());
      const res = await axios.post(`${API}/upload/gpx`, fd, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setResult(res.data);
    } catch (err) {
      const msg = err?.response?.data?.detail;
      setError(typeof msg === "string" ? msg : "Kunde inte tolka GPX-filen.");
    }
    setBusy(false);
    e.target.value = "";
  };

  const bbox = result?.stats?.bbox;
  const fitBounds = bbox ? [[bbox[0], bbox[1]], [bbox[2], bbox[3]]] : null;
  const center = bbox ? [(bbox[0] + bbox[2]) / 2, (bbox[1] + bbox[3]) / 2] : [16.362, 63.878];

  return (
    <div data-testid="gpx-verktyg" className="max-w-5xl mx-auto px-4 sm:px-6 py-10 sm:py-14">
      <Link to="/mod" className="inline-flex items-center gap-1 text-bark hover:text-pine text-sm no-underline mb-4" data-testid="link-tillbaka-mod">
        <ArrowLeft size={14} /> Tillbaka till moderationen
      </Link>
      <h1 className="font-fraktur-smooth text-2xl sm:text-3xl text-falu tracking-wide mb-2">GPX-verktyg</h1>
      <p className="text-sm text-bark mb-8 leading-relaxed">
        Ladda upp ett GPX-spår för förhandsvisning på kartan. Du kan växla mellan standardkarta och topografisk karta.
        Filen lagras säkert i Cloudflare R2 och kan länkas i en led-detalj.
      </p>

      {/* Build 94c.5 — Arbeitsanweisung + Sicherheitsbestätigung */}
      <div data-testid="gpx-warning-banner" className="mb-5 px-4 py-3 bg-amber-50 border-l-4 border-amber-500 rounded-sm text-sm text-amber-900">
        <div className="flex items-start gap-2">
          <AlertTriangle size={18} className="shrink-0 mt-0.5" />
          <div>
            <strong>Använd GPX-verktyget bara när källa, område och rättigheter är kontrollerade.</strong>
            <p className="mt-1 text-amber-800">Filen sparas i Cloudflare R2 och blir publik via URL. Kontrollera alltid kartan efter import. Notera källa, område och kategori i fältet nedan — audit-loggen sparar vem och när.</p>
          </div>
        </div>
      </div>
      <div className="mb-4 grid grid-cols-1 sm:grid-cols-2 gap-3">
        <label className="text-sm flex flex-col gap-1">
          <span className="font-medium text-bark">Källa / område / led-namn (sparas i audit)</span>
          <input
            type="text"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="t.ex. Källa: Strömsunds kommun. Område: Jansjö. Led: Flobergsstigen."
            data-testid="gpx-note-input"
            className="px-3 py-2 border border-warm-border rounded text-sm bg-white"
          />
        </label>
        <label className="text-sm flex items-start gap-2 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={confirmed}
            onChange={(e) => setConfirmed(e.target.checked)}
            data-testid="gpx-confirm-checkbox"
            className="mt-1"
          />
          <span className="text-bark">Jag bekräftar att <strong>källa, område och rättigheter</strong> är kontrollerade. (Krävs innan filen kan väljas.)</span>
        </label>
      </div>

      <div className="flex flex-wrap items-center gap-3 mb-6">
        <label
          data-testid="gpx-upload-btn"
          className={`inline-flex items-center gap-2 font-body font-semibold text-sm px-5 py-3 rounded-sm transition-colors ${confirmed ? "bg-falu hover:bg-falu-hover text-parchment cursor-pointer" : "bg-bone-alt text-bark/40 cursor-not-allowed"}`}
        >
          <Upload size={16} />
          {busy ? "Tolkar..." : "Välj .gpx-fil"}
          <input type="file" accept=".gpx,application/gpx+xml" className="hidden" onChange={handleFile} disabled={busy || !confirmed} />
        </label>
        <div className="inline-flex border border-warm-border rounded-sm overflow-hidden" data-testid="gpx-layer-switcher">
          <button
            onClick={() => setTileLayer("standard")}
            className={`px-3 py-2 text-xs font-semibold ${tileLayer === "standard" ? "bg-pine text-parchment" : "bg-white text-ink hover:bg-bone-alt"}`}
            data-testid="layer-standard"
          >Standardkarta</button>
          <button
            onClick={() => setTileLayer("topo")}
            className={`px-3 py-2 text-xs font-semibold border-l border-warm-border ${tileLayer === "topo" ? "bg-pine text-parchment" : "bg-white text-ink hover:bg-bone-alt"}`}
            data-testid="layer-topo"
          >Topografisk</button>
        </div>
      </div>

      {error && <p className="text-falu text-sm mb-4" data-testid="gpx-error">{error}</p>}

      {result && (
        <>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-4" data-testid="gpx-stats">
            <Stat icon={<Ruler size={16} />} label="Sträcka" value={`${result.stats.distance_km} km`} />
            <Stat icon={<MapPin size={16} />} label="Punkter" value={result.stats.points} />
            <Stat icon={<ArrowUp size={16} />} label="Högsta" value={result.stats.max_elevation_m != null ? `${result.stats.max_elevation_m} m` : "–"} />
            <Stat icon={<ArrowDown size={16} />} label="Lägsta" value={result.stats.min_elevation_m != null ? `${result.stats.min_elevation_m} m` : "–"} />
          </div>

          <div className="relative w-full h-[520px] rounded-sm overflow-hidden border border-warm-border bg-parchment mb-4" data-testid="gpx-map-container">
            <RegionMap
              variant="page"
              center={center}
              zoom={12}
              gpxTracks={[{ name: result.url, geojson: result.geojson, color: "#B05B41" }]}
              fitBounds={fitBounds}
              tileLayer={tileLayer}
              showLayerSwitcher={false}
              className="absolute inset-0"
            />
          </div>

          <div className="bg-bone-alt border border-warm-border rounded-sm p-4 text-sm">
            <p className="text-bark mb-2"><strong>Lagrad fil:</strong></p>
            <code className="block text-xs text-ink/70 bg-white border border-warm-border rounded-sm px-3 py-2 break-all" data-testid="gpx-stored-url">
              {result.url}
            </code>
            <p className="text-xs text-bark mt-2">
              Kopiera URL:en och referera till den från en led-detaljsida för att visa spåret för besökare.
            </p>
          </div>
        </>
      )}
    </div>
  );
}

function Stat({ icon, label, value }) {
  return (
    <div className="bg-white border border-warm-border rounded-sm px-3 py-2">
      <div className="flex items-center gap-1 text-bark text-xs mb-0.5">{icon} {label}</div>
      <div className="font-heading text-lg text-ink">{value}</div>
    </div>
  );
}
